import { useState, useRef, useEffect } from "react";
import { Form, Button, Table, Badge, Spinner, Dropdown } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { uploadDocumentRequest, uploadDocumentFilesRequest, deleteDocumentRequest } from "@/services";
import { formatApiErrorMessage } from "@/services/_apiConfig";
import DocumentFiles from "../document_files";
import { uploadEachFile, describeUploadFailures } from "../upload_utils";

const formatYearLabel = (year) => (/^FY\d{4}$/.test(year) ? `FY ${year.slice(2)}` : year);

const FinancialStatementsUploadForm = ({
  doc,
  documentsList = [],
  reloadDocumentsList,
  required = false,
  validationError,
  onUploadingChange,
}) => {
  const { applicationId: applicationUuid } = useParams();
  const [addedYears, setAddedYears] = useState([]);
  const [uploadingYear, setUploadingYear] = useState(null);
  const [deletingYear, setDeletingYear] = useState(null);
  const [rowError, setRowError] = useState({ year: null, message: "" });
  const fileInputsRef = useRef({});

  const typeDocs = documentsList.filter((d) => d.document_type === doc.document_type);
  const docByYear = {};
  typeDocs.forEach((d) => {
    const year = d.metadata?.financial_year;
    if (year) docByYear[year] = d;
  });
  const filesWithoutYear = typeDocs
    .filter((d) => !d.metadata?.financial_year)
    .flatMap((d) => d.document_files || []);

  const requiredYears = required ? doc.required_financial_years || [] : [];
  const requiredYearsKey = requiredYears.join(",");

  // Pre-fill the required years once they apply; the customer can still remove them.
  useEffect(() => {
    if (!requiredYearsKey) return;
    setAddedYears((prev) => [...new Set([...prev, ...requiredYearsKey.split(",")])]);
  }, [requiredYearsKey]);

  const rows = [...new Set([...Object.keys(docByYear), ...addedYears])].sort();
  const availableYears = (doc.financial_year_options || [])
    .filter((year) => !rows.includes(year))
    .sort()
    .reverse();

  const filesFor = (year) => docByYear[year]?.document_files || [];

  const ensureYearDocument = async (year) => {
    if (docByYear[year]) return docByYear[year].id;
    const formData = new FormData();
    formData.append("loan_id", applicationUuid);
    formData.append("document_type_id", doc.document_type);
    formData.append("metadata", JSON.stringify({ financial_year: year }));
    const res = await uploadDocumentRequest(formData);
    if (res?.success === false) {
      throw new Error(formatApiErrorMessage(res.error, "Could not save the financial year. Try again."));
    }
    return res?.results?.document_id;
  };

  const handleUpload = async (e, year) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingYear(year);
    onUploadingChange?.(true);
    setRowError({ year: null, message: "" });
    try {
      const documentId = await ensureYearDocument(year);
      const failures = await uploadEachFile(files, (file) => {
        const uploadData = new FormData();
        uploadData.append("file", file);
        return uploadDocumentFilesRequest(documentId, uploadData);
      });
      setRowError({ year, message: describeUploadFailures(failures, files.length) });
      await reloadDocumentsList();
    } catch (err) {
      setRowError({ year, message: err?.message || "File upload failed. Try again." });
    } finally {
      if (fileInputsRef.current[year]) fileInputsRef.current[year].value = "";
      setUploadingYear(null);
      onUploadingChange?.(false);
    }
  };

  const handleRemoveYear = async (year) => {
    const yearDoc = docByYear[year];
    if (!yearDoc) {
      setAddedYears((prev) => prev.filter((y) => y !== year));
      return;
    }
    if (!window.confirm(`Remove ${formatYearLabel(year)}? This will delete its uploaded statements.`)) return;

    setDeletingYear(year);
    try {
      const res = await deleteDocumentRequest(yearDoc.uuid);
      if (res?.success === false) {
        setRowError({ year, message: "Failed to remove this year. Try again." });
        return;
      }
      setAddedYears((prev) => prev.filter((y) => y !== year));
      await reloadDocumentsList();
    } finally {
      setDeletingYear(null);
    }
  };

  const statusBadge = (year) => {
    if (filesFor(year).length) return <Badge bg="success">Uploaded</Badge>;
    if (requiredYears.includes(year)) return <Badge bg="danger">Required</Badge>;
    return <Badge bg="secondary">Pending</Badge>;
  };

  return (
    <div className="mb-4 border rounded p-3">
      <h6>
        {doc.title} {required && <span className="text-danger">*</span>}
      </h6>
      {doc.description && <p className="text-muted">{doc.description}</p>}
      {validationError && (
        <p className="text-danger mb-2" role="alert">{validationError}</p>
      )}

      {rows.length > 0 && (
        <Table bordered responsive className="align-middle mb-3">
          <thead>
            <tr>
              <th style={{ width: "18%" }}>Financial Year</th>
              <th>Document</th>
              <th style={{ width: "14%" }}>Status</th>
              <th style={{ width: "6%" }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((year) => (
              <tr key={year} data-testid={`fincobox-financials-row-${year}`}>
                <td>{formatYearLabel(year)}</td>
                <td>
                  <Form.Control
                    type="file"
                    className="file-input--hide-chosen-text"
                    aria-label={`Upload financial statements for ${formatYearLabel(year)}`}
                    multiple={doc.multi_upload}
                    ref={(el) => (fileInputsRef.current[year] = el)}
                    onChange={(e) => handleUpload(e, year)}
                    disabled={!!uploadingYear}
                  />
                  {uploadingYear === year && (
                    <div className="d-flex align-items-center mt-2 text-muted" role="status">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Uploading file...</span>
                    </div>
                  )}
                  {rowError.year === year && rowError.message && (
                    <p className="text-danger small mt-2 mb-0" role="alert">{rowError.message}</p>
                  )}
                  <DocumentFiles files={filesFor(year)} reloadDocumentsList={reloadDocumentsList} />
                </td>
                <td>{statusBadge(year)}</td>
                <td className="text-center">
                  <Button
                    variant="link"
                    className="p-0 text-danger"
                    title={`Remove ${formatYearLabel(year)}`}
                    onClick={() => handleRemoveYear(year)}
                    disabled={deletingYear === year}
                  >
                    {deletingYear === year ? <Spinner animation="border" size="sm" /> : <BsTrash size={16} />}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {availableYears.length > 0 && (
        <Dropdown onSelect={(year) => year && setAddedYears((prev) => [...prev, year])}>
          <Dropdown.Toggle variant="outline-primary" data-testid="fincobox-financials-add-year">
            + Add financial year
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {availableYears.map((year) => (
              <Dropdown.Item key={year} eventKey={year}>{formatYearLabel(year)}</Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}

      {filesWithoutYear.length > 0 && (
        <div className="mt-3">
          <p className="text-muted small mb-1">Others (uploaded without a financial year)</p>
          <DocumentFiles files={filesWithoutYear} reloadDocumentsList={reloadDocumentsList} />
        </div>
      )}
    </div>
  );
};

export default FinancialStatementsUploadForm;
